// users-delegation §6: מסך "משתמשים" — ל-admin ולבעל "הרשאת האצלה". טבלת שורות (גם במובייל), חיפוש, "הוספת משתמש", מגירת משתמש
// (תפקיד, השבתה/הפעלה, הרשאות עם מתגים נעולים, גישה לאינטרנט, קישור כניסה, שיתוף יצירה; ל-admin גם מתג "הרשאת האצלה"), ולשונית
// "יומן". כל לחיצה היא פעולה של אדם (בלי קונסנט נוסף); כל הגבלה נאכפת בשרת, ושגיאה מוצגת כהודעת השרת. גישה בלי הרשאה → 403 של השרת.
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Role } from '@aibify/core';
import { useSession } from '../lib/auth.ts';
import { T } from '../lib/strings.ts';
import { cn } from '../lib/utils.ts';
import { fmtWhen } from '../lib/time.ts';
import { usersApi, type UserAccess, type UserRow } from '../lib/usersApi.ts';
import { EmptyState, ErrorView, Loading } from '../components/Status.tsx';
import { Button } from '../components/ui/button.tsx';
import { Badge, StatusLabel } from '../components/ui/badge.tsx';
import { Input, Select } from '../components/ui/input.tsx';
import { Sheet } from '../components/ui/sheet.tsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs.tsx';
import { DataAccessSection } from '../components/DataAccess.tsx';
import { IconCheck, IconHistory, IconLock, IconPlus, IconSearch, IconUsers, IconX } from '../components/brand/icons.tsx';

const ROLE_ORDER: Role[] = ['member', 'creator', 'admin'];
const TH = 'px-3 py-2 text-start font-semibold whitespace-nowrap';
const TD = 'px-3 py-2 align-middle';

export function UsersPage() {
  const me = useQuery({ queryKey: ['users-me'], queryFn: usersApi.me, retry: false });
  const list = useQuery({ queryKey: ['users'], queryFn: usersApi.list, retry: false });
  const [tab, setTab] = useState('users');
  const [text, setText] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const isAdmin = !!me.data?.isAdmin;

  if (list.isError) return <ErrorView error={list.error} />;
  const all = list.data ?? [];
  const needle = text.trim().toLowerCase();
  const rows = all.filter((u) => !needle || `${u.nameHe} ${u.email}`.toLowerCase().includes(needle));

  return (
    <section className="flex flex-col gap-5" data-testid="users-page">
      <header className="flex flex-wrap items-end gap-x-6 gap-y-2">
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className="page-title">{T.users.title}</h1>
          <p className="m-0 max-w-3xl text-muted-foreground">{me.data?.isDelegate ? T.users.subtitleDelegate : T.users.subtitle}</p>
        </div>
        <Button className="ms-auto" onClick={() => setAdding(true)} data-testid="users-add">
          <IconPlus size={18} />
          {T.users.add}
        </Button>
      </header>

      <Tabs value={tab} onValueChange={setTab} dir="rtl">
        <TabsList>
          <TabsTrigger value="users" data-testid="tab-users">
            <IconUsers size={18} />
            {T.users.tabUsers}
          </TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-log">
            <IconHistory size={18} />
            {T.users.tabLog}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <div className="relative min-w-0 flex-1 sm:max-w-md">
              <IconSearch size={18} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input type="search" className="ps-10" placeholder={T.users.search} aria-label={T.users.search} value={text} onChange={(e) => setText(e.target.value)} data-testid="users-search" />
            </div>
            {list.data && <span className="ms-auto text-sm text-muted-foreground">{T.users.count(rows.length, all.length)}</span>}
          </div>
          {list.isPending && <Loading variant="rows" />}
          {list.data && rows.length === 0 && <EmptyState icon={<IconSearch size={22} />} title={T.users.empty} />}
          {rows.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-border bg-card shadow-card max-md:overflow-visible">
              <table className="w-full border-collapse text-start max-md:block" data-testid="user-rows">
                <thead className="border-b border-border bg-muted/50 text-[13px] text-muted-foreground max-md:hidden">
                  <tr>
                    <th scope="col" className={TH}>{T.users.col.name}</th>
                    <th scope="col" className={TH}>{T.users.col.role}</th>
                    <th scope="col" className={TH}>{T.users.col.status}</th>
                    <th scope="col" className={TH}>{T.users.col.createdBy}</th>
                    {isAdmin && <th scope="col" className={TH}>{T.users.col.delegation}</th>}
                  </tr>
                </thead>
                <tbody className="max-md:block max-md:divide-y max-md:divide-border">
                  {rows.map((u) => (
                    <Row key={u.id} u={u} isAdmin={isAdmin} onOpen={() => setOpen(u.id)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
        <TabsContent value="log">{tab === 'log' && <LogList mine={!!me.data?.isDelegate} />}</TabsContent>
      </Tabs>

      <Sheet open={!!open} onClose={() => setOpen(null)} title={T.users.title} side="start" className="max-w-lg max-md:max-w-none" testId="user-drawer">
        {open && <UserDrawer id={open} isAdmin={isAdmin} onClose={() => setOpen(null)} />}
      </Sheet>
      <Sheet open={adding} onClose={() => setAdding(false)} title={T.users.add} side="start" className="max-w-md max-md:max-w-none" testId="user-add-drawer">
        {adding && <AddUser isAdmin={isAdmin} onClose={() => setAdding(false)} onOpen={(id) => { setAdding(false); setOpen(id); }} />}
      </Sheet>
    </section>
  );
}

/** שורה. שורה שאי אפשר לשנות (מנהל מערכת לבעל האצלה, או החשבון שלך) — אפורה; ה-admin פותח גם אותה (למתג ההאצלה). */
function Row({ u, isAdmin, onOpen }: { u: UserRow; isAdmin: boolean; onOpen: () => void }) {
  const locked = !u.editable && !isAdmin;
  return (
    <tr
      onClick={locked ? undefined : onOpen}
      className={cn(
        'border-b border-border last:border-b-0 max-md:flex max-md:min-h-11 max-md:flex-wrap max-md:items-center max-md:gap-x-2 max-md:gap-y-1.5 max-md:border-b-0 max-md:px-3 max-md:py-2.5',
        locked ? 'text-muted-foreground opacity-60' : 'cursor-pointer transition-colors hover:bg-muted/60',
      )}
      data-testid={`user-row-${u.email}`}
      data-locked={locked ? '1' : '0'}
      title={locked ? T.users.readOnly : undefined}
    >
      <td className={cn(TD, 'max-md:order-1 max-md:block max-md:min-w-0 max-md:flex-1 max-md:p-0')}>
        <span className="flex min-w-0 flex-col">
          {locked ? (
            <span className="truncate font-bold">{u.nameHe}</span>
          ) : (
            <button type="button" className="cursor-pointer truncate text-start font-bold hover:text-primary" onClick={(e) => { e.stopPropagation(); onOpen(); }} data-testid="user-open">
              {u.nameHe}
            </button>
          )}
          <span className="truncate text-[13px] text-muted-foreground" dir="ltr" style={{ textAlign: 'start' }}>{u.email}</span>
        </span>
      </td>
      <td className={cn(TD, 'whitespace-nowrap max-md:order-2 max-md:block max-md:p-0')}>
        <Badge tone={u.role === 'admin' ? 'info' : 'neutral'} className="gap-1">
          {locked && <IconLock size={12} />}
          {T.users.roles[u.role]}
        </Badge>
      </td>
      <td aria-hidden="true" className="hidden max-md:order-3 max-md:block max-md:h-0 max-md:basis-full max-md:p-0" />
      <td className={cn(TD, 'whitespace-nowrap max-md:order-4 max-md:block max-md:p-0')}>
        <StatusLabel tone={u.active ? 'ok' : 'neutral'}>{u.active ? T.users.active : T.users.disabled}</StatusLabel>
      </td>
      <td className={cn(TD, 'text-sm whitespace-nowrap text-muted-foreground max-md:order-5 max-md:block max-md:p-0 max-md:text-[13px]')}>{u.createdBy?.nameHe ?? ''}</td>
      {isAdmin && (
        <td className={cn(TD, 'whitespace-nowrap max-md:order-6 max-md:block max-md:p-0')}>
          {u.delegate && (
            <Badge tone="info" data-testid="row-delegate">
              {T.users.delegate}
            </Badge>
          )}
        </td>
      )}
    </tr>
  );
}

function Section({ title, children, testId }: { title: string; children: React.ReactNode; testId?: string }) {
  return (
    <section className="flex flex-col gap-2 border-b border-border pb-4 last:border-b-0" data-testid={testId}>
      <h3 className="m-0 text-sm font-semibold text-foreground/90">{title}</h3>
      {children}
    </section>
  );
}

function useAct() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const run = async <T,>(fn: () => Promise<T>): Promise<T | null> => {
    setErr(null);
    setBusy(true);
    try {
      const r = await fn();
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['user-access'] });
      void qc.invalidateQueries({ queryKey: ['users-log'] });
      return r;
    } catch (e) {
      setErr(e);
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { busy, err, run };
}

function AddUser({ isAdmin, onClose, onOpen }: { isAdmin: boolean; onClose: () => void; onOpen: (id: string) => void }) {
  const s = useSession();
  const roles = ROLE_ORDER.filter((r) => isAdmin || (r !== 'admin' && ROLE_ORDER.indexOf(r) <= ROLE_ORDER.indexOf(s?.user.role ?? 'member')));
  const [email, setEmail] = useState('');
  const [nameHe, setName] = useState('');
  const [role, setRole] = useState<Role>('member');
  const [done, setDone] = useState<{ id: string; nameHe: string; role: Role; secret: string } | null>(null);
  const a = useAct();
  const users = useQuery({ queryKey: ['users'], queryFn: usersApi.list, retry: false, enabled: !!done });
  return (
    <div className="flex flex-col gap-4 p-4" data-testid="user-add">
      <DrawerHead title={T.users.add} onClose={onClose} />
      {!done ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void a.run(() => usersApi.create({ email: email.trim(), nameHe: nameHe.trim(), role })).then((r) => r && setDone({ id: r.user.id, nameHe: r.user.nameHe, role: r.user.role, secret: r.secret.value }));
          }}
        >
          <label className="flex flex-col gap-1 text-sm font-semibold">
            {T.users.form.email}
            <Input type="email" dir="ltr" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="add-email" />
          </label>
          <label className="flex flex-col gap-1 text-sm font-semibold">
            {T.users.form.name}
            <Input required value={nameHe} onChange={(e) => setName(e.target.value)} data-testid="add-name" />
          </label>
          <label className="flex flex-col gap-1 text-sm font-semibold">
            {T.users.form.role}
            <Select value={role} onChange={(e) => setRole(e.target.value as Role)} data-testid="add-role">
              {roles.map((r) => (
                <option key={r} value={r}>
                  {T.users.roles[r]}
                </option>
              ))}
            </Select>
          </label>
          {a.err != null && <ErrorView error={a.err} />}
          <div className="flex gap-2">
            <Button type="submit" disabled={a.busy} data-testid="add-submit">
              {a.busy ? T.users.form.busy : T.users.form.submit}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>
              {T.users.form.cancel}
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex flex-col gap-3" data-testid="add-done">
          <p className="m-0 inline-flex items-center gap-2 font-semibold text-ok">
            <IconCheck size={18} />
            {T.users.created(done.nameHe)}
          </p>
          {/* data-access-rls §7.1: מיד אחרי היצירה, "מה הוא יראה?" למשתמש החדש (מנהל מערכת רואה תמיד הכול; אין מה לבחור) */}
          {done.role !== 'admin' && (
            <div className="rounded-md border border-border bg-muted/30 p-3" data-testid="add-data-access">
              <DataAccessSection userId={done.id} isAdmin={isAdmin} users={users.data ?? []} guided />
            </div>
          )}
          <OnceValue label={T.users.secretTitle} value={done.secret} testId="add-secret" />
          <p className="m-0 text-sm text-muted-foreground">{T.users.secretHint}</p>
          <Button variant="outline" className="w-fit" onClick={() => onOpen(done.id)} data-testid="add-open">
            {T.users.sections.link}
          </Button>
        </div>
      )}
    </div>
  );
}

function DrawerHead({ title, sub, onClose }: { title: string; sub?: string; onClose: () => void }) {
  return (
    <div className="-mx-4 -mt-4 flex items-center gap-2 border-b border-border p-3 ps-4">
      <IconUsers size={20} className="shrink-0 text-primary" />
      <div className="me-auto flex min-w-0 flex-col">
        <h2 className="m-0 truncate text-lg font-bold">{title}</h2>
        {sub && <span className="truncate text-[13px] text-muted-foreground" dir="ltr" style={{ textAlign: 'start' }}>{sub}</span>}
      </div>
      <Button variant="ghost" size="icon" aria-label={T.users.close} title={T.users.close} onClick={onClose} data-testid="drawer-close">
        <IconX size={20} />
      </Button>
    </div>
  );
}

/** ערך שמוצג פעם אחת (סיסמה זמנית / קישור כניסה) עם העתקה. */
function OnceValue({ label, value, testId }: { label: string; value: string; testId: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-col gap-1 rounded-md border border-border bg-muted p-3" data-testid={testId}>
      <span className="text-sm font-semibold">{label}</span>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate text-sm" dir="ltr">
          {value}
        </code>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            void navigator.clipboard?.writeText(value).catch(() => undefined);
            setCopied(true);
          }}
        >
          {copied ? T.users.copied : T.users.copy}
        </Button>
      </div>
    </div>
  );
}

function UserDrawer({ id, isAdmin, onClose }: { id: string; isAdmin: boolean; onClose: () => void }) {
  const q = useQuery({ queryKey: ['user-access', id], queryFn: () => usersApi.access(id), retry: false, staleTime: 0 });
  if (q.isPending) return <div className="p-4"><Loading /></div>;
  if (q.isError) return <div className="p-4"><ErrorView error={q.error} /></div>;
  return <UserBody a={q.data} isAdmin={isAdmin} onClose={onClose} />;
}

function UserBody({ a, isAdmin, onClose }: { a: UserAccess; isAdmin: boolean; onClose: () => void }) {
  const u = a.user;
  const act = useAct();
  const [link, setLink] = useState<string | null>(null);
  const [hosts, setHosts] = useState('');
  const [until, setUntil] = useState('');
  const [pick, setPick] = useState('');
  const users = useQuery({ queryKey: ['users'], queryFn: usersApi.list, retry: false });
  const shareables = useQuery({ queryKey: ['users-shareables'], queryFn: usersApi.shareables, enabled: a.editable, retry: false });
  const maxUntil = useMemo(() => {
    if (!a.myScopes) return null;
    const ds = a.myScopes.map((s) => s.until).filter((x): x is string => !!x).sort();
    return ds.length ? ds.at(-1)! : null;
  }, [a.myScopes]);
  const ed = a.editable;
  return (
    <div className="flex flex-col gap-4 p-4" data-testid="user-body" data-user={u.email}>
      <DrawerHead title={u.nameHe} sub={u.email} onClose={onClose} />
      {!ed && <p className="m-0 rounded-md bg-muted p-3 text-sm" data-testid="user-readonly">{T.users.selfNote}</p>}
      {act.err != null && <ErrorView error={act.err} />}

      <Section title={T.users.sections.role}>
        <Select
          aria-label={T.users.sections.role}
          value={u.role}
          disabled={!ed || act.busy}
          onChange={(e) => void act.run(() => usersApi.setRole(u.id, e.target.value as Role))}
          data-testid="user-role"
        >
          {ROLE_ORDER.filter((r) => a.roles.includes(r) || r === u.role).map((r) => (
            <option key={r} value={r} disabled={!a.roles.includes(r)}>
              {T.users.roles[r]}
            </option>
          ))}
        </Select>
      </Section>

      <Section title={T.users.sections.status}>
        <div className="flex items-center gap-3">
          <StatusLabel tone={u.active ? 'ok' : 'neutral'}>{u.active ? T.users.active : T.users.disabled}</StatusLabel>
          {ed && (
            <Button size="sm" variant={u.active ? 'destructive' : 'outline'} disabled={act.busy} onClick={() => void act.run(() => usersApi.setActive(u.id, !u.active))} data-testid="user-active">
              {u.active ? T.users.deactivate : T.users.reactivate}
            </Button>
          )}
        </div>
      </Section>

      <Section title={T.users.sections.perms} testId="user-perms">
        <ul className="m-0 flex list-none flex-col divide-y divide-border overflow-hidden rounded-md border border-border bg-card p-0">
          {a.interfaces.map((i) => (
            <li key={i.id} className={cn('flex min-h-11 items-center gap-3 px-3 py-1.5', !i.grantable && 'text-muted-foreground')} data-iface={i.id} data-locked={i.grantable ? '0' : '1'}>
              <span className="me-auto min-w-0 truncate">{i.nameHe}</span>
              {!i.grantable && (
                <span className="inline-flex items-center gap-1 text-[13px]" data-testid="perm-locked">
                  <IconLock size={14} />
                  {T.users.noPerm}
                </span>
              )}
              <input
                type="checkbox"
                role="switch"
                aria-label={i.nameHe}
                className="size-5 shrink-0 accent-[hsl(var(--primary))] disabled:opacity-50"
                checked={i.granted}
                disabled={!i.grantable || act.busy}
                onChange={(e) => void act.run(() => usersApi.setGrant(u.id, i.id, e.target.checked))}
                data-testid={`perm-${i.id}`}
              />
            </li>
          ))}
        </ul>
      </Section>

      {/* data-access-rls §7: עולם הנתונים של המשתמש (בתוך מה שהצופה עצמו רואה) */}
      {(ed || isAdmin) && (
        <Section title={T.users.da.title} testId="user-data-access">
          <DataAccessSection userId={u.id} isAdmin={isAdmin} users={users.data ?? []} />
        </Section>
      )}

      <Section title={T.users.sections.web} testId="user-web">
        {a.scopes.length ? (
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {a.scopes.map((s) => {
              const h = s.hosts === '*' ? T.users.webAll : (s.hosts ?? []).join(', ');
              return (
                <li key={s.id} className="flex min-h-11 items-center gap-2 rounded-md border border-border px-3">
                  <span className="me-auto min-w-0 truncate" dir="ltr" style={{ textAlign: 'start' }}>{h}</span>
                  {s.until && <span className="text-[13px] text-muted-foreground">{T.users.until(fmtWhen(s.until))}</span>}
                  {ed && (
                    <Button variant="ghost" size="icon" aria-label={T.users.remove(h)} title={T.users.remove(h)} onClick={() => void act.run(() => usersApi.removeScope(u.id, s.id))}>
                      <IconX size={16} />
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="m-0 text-sm text-muted-foreground">{T.users.webNone}</p>
        )}
        {ed && (
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const hs = hosts.trim() === '*' ? ('*' as const) : hosts.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean);
              const end = until ? new Date(`${until}T23:59:00`).toISOString() : '';
              void act.run(() => usersApi.addScope(u.id, { hosts: hs, until: end })).then((r) => r && (setHosts(''), setUntil('')));
            }}
          >
            <label className="flex min-w-0 flex-1 flex-col gap-1 text-[13px]">
              {T.users.webHosts}
              <Input dir="ltr" value={hosts} onChange={(e) => setHosts(e.target.value)} placeholder="data.gov.il" data-testid="web-hosts" />
            </label>
            <label className="flex flex-col gap-1 text-[13px]">
              {T.users.webUntil}
              <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} {...(maxUntil ? { max: maxUntil.slice(0, 10) } : {})} data-testid="web-until" />
            </label>
            <Button type="submit" variant="outline" disabled={!hosts.trim() || act.busy} data-testid="web-add">
              {T.users.webAdd}
            </Button>
            {maxUntil && <p className="m-0 basis-full text-[13px] text-muted-foreground">{T.users.webUntilMax(fmtWhen(maxUntil))}</p>}
          </form>
        )}
      </Section>

      {ed && u.active && (
        <Section title={T.users.sections.link} testId="user-link">
          {link ? (
            <OnceValue label={T.users.sections.link} value={link} testId="link-value" />
          ) : (
            <Button variant="outline" className="w-fit" disabled={act.busy} onClick={() => void act.run(() => usersApi.loginLink(u.id)).then((r) => r && setLink(r.url))} data-testid="link-make">
              {T.users.linkMake}
            </Button>
          )}
          <p className="m-0 text-[13px] text-muted-foreground">{T.users.linkHint}</p>
        </Section>
      )}

      <Section title={T.users.sections.share} testId="user-share">
        {a.shares.length ? (
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {a.shares.map((s) => (
              <li key={s.slug} className="flex min-h-11 items-center gap-2 rounded-md border border-border px-3">
                <span className="me-auto min-w-0 truncate">{s.nameHe}</span>
                {s.sharedBy && <span className="text-[13px] text-muted-foreground">{T.users.sharedBy(s.sharedBy.nameHe)}</span>}
                {ed && (
                  <Button variant="ghost" size="icon" aria-label={T.users.remove(s.nameHe)} title={T.users.remove(s.nameHe)} onClick={() => void act.run(() => usersApi.unshare(u.id, s.slug))}>
                    <IconX size={16} />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="m-0 text-sm text-muted-foreground">{T.users.shareNone}</p>
        )}
        {ed && (
          <div className="flex gap-2">
            <Select aria-label={T.users.sharePick} value={pick} onChange={(e) => setPick(e.target.value)} data-testid="share-artifact">
              <option value="">{T.users.sharePickNone}</option>
              {(shareables.data ?? []).filter((x) => !a.shares.some((s) => s.slug === x.slug)).map((x) => (
                <option key={x.slug} value={x.slug}>
                  {x.nameHe}
                </option>
              ))}
            </Select>
            <Button variant="outline" disabled={!pick || act.busy} onClick={() => void act.run(() => usersApi.share(u.id, pick)).then((r) => r && setPick(''))} data-testid="share-artifact-add">
              {T.users.shareAdd}
            </Button>
          </div>
        )}
      </Section>

      {isAdmin && u.role !== 'admin' && (
        <Section title={T.users.sections.delegation} testId="user-delegation">
          <label className="flex min-h-11 cursor-pointer items-start gap-3 rounded-md border border-border bg-card p-3">
            <input
              type="checkbox"
              role="switch"
              className="mt-1 size-5 shrink-0 accent-[hsl(var(--primary))]"
              checked={a.delegate}
              disabled={act.busy}
              onChange={(e) => void act.run(() => usersApi.setDelegation(u.id, e.target.checked))}
              data-testid="delegation-toggle"
            />
            <span className="flex flex-col">
              <span className="font-semibold">{T.users.delegationOn}</span>
              <span className="text-sm text-muted-foreground" data-testid="delegation-hint">{T.users.delegationHint(u.nameHe)}</span>
            </span>
          </label>
        </Section>
      )}
    </div>
  );
}

function LogList({ mine }: { mine: boolean }) {
  const q = useQuery({ queryKey: ['users-log'], queryFn: () => usersApi.log(), retry: false });
  if (q.isPending) return <Loading variant="rows" />;
  if (q.isError) return <ErrorView error={q.error} onRetry={() => q.refetch()} />;
  return (
    <div className="flex flex-col gap-2" data-testid="users-log">
      <p className="m-0 text-sm text-muted-foreground">{mine ? T.users.log.mine : T.users.log.all}</p>
      {q.data.length === 0 ? (
        <EmptyState icon={<IconHistory size={22} />} title={T.users.log.empty} />
      ) : (
        <ol className="m-0 flex list-none flex-col divide-y divide-border overflow-hidden rounded-md border border-border bg-card p-0 shadow-card">
          {q.data.map((e) => (
            <li key={e.id} className="flex flex-col gap-0.5 px-3 py-2 sm:flex-row sm:items-baseline sm:gap-3" data-testid="log-row" data-action={e.action}>
              <span className="shrink-0 text-[13px] whitespace-nowrap text-muted-foreground tabular-nums">{fmtWhen(e.at)}</span>
              <span className="min-w-0">{e.textHe}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
